import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { clearDataCache } from "../lib/useCachedList";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithPassword = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setUser(res.data);
    return res.data;
  };
  const register = async (payload) => (await api.post("/auth/register", payload)).data;
  const verifyEmail = async (token) => {
    const res = await api.post("/auth/verify-email", { token });
    setUser(res.data);
    return res.data;
  };
  const resendVerification = async (email) => (await api.post("/auth/resend-verification", { email })).data;
  const forgotPassword = async (email) => (await api.post("/auth/forgot-password", { email })).data;
  const resetPassword = async (token, new_password) => (await api.post("/auth/reset-password", { token, new_password })).data;

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore
    }
    clearDataCache();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, setUser, checkAuth, loginWithGoogle, loginWithPassword, register, verifyEmail, resendVerification, forgotPassword, resetPassword, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
